/**
 * Promise Timeout Utility
 * Prevents operations from hanging indefinitely by adding timeout protection
 */

export class TimeoutError extends Error {
  constructor(
    message: string,
    public readonly timeoutMs: number
  ) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Wraps a promise with a timeout
 * If the promise doesn't resolve/reject within the timeout period, it will reject with a TimeoutError
 *
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMessage - Custom error message for timeout
 * @returns Promise that resolves/rejects with the original promise or times out
 *
 * @example
 * ```typescript
 * try {
 *   const result = await withTimeout(
 *     fetch('https://api.example.com'),
 *     5000,
 *     'API request timeout'
 *   );
 * } catch (error) {
 *   if (error instanceof TimeoutError) {
 *     console.log('Operation timed out after', error.timeoutMs, 'ms');
 *   }
 * }
 * ```
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string = 'Operation timed out'
): Promise<T> {
  let timeoutHandle: NodeJS.Timeout;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(new TimeoutError(`${errorMessage} (after ${timeoutMs}ms)`, timeoutMs));
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeoutPromise]);
    clearTimeout(timeoutHandle!);
    return result;
  } catch (error) {
    clearTimeout(timeoutHandle!);
    throw error;
  }
}

/**
 * Creates a function that wraps async operations with a timeout
 * Useful for wrapping service methods
 *
 * @param timeoutMs - Default timeout in milliseconds
 * @param errorMessage - Default error message
 * @returns Function that wraps promises with timeout
 *
 * @example
 * ```typescript
 * const withFiveSecondTimeout = createTimeoutWrapper(5000, 'Operation timeout');
 * const result = await withFiveSecondTimeout(someAsyncOperation());
 * ```
 */
export function createTimeoutWrapper(
  timeoutMs: number,
  errorMessage: string = 'Operation timed out'
) {
  return <T>(promise: Promise<T>, customMessage?: string): Promise<T> =>
    withTimeout(promise, timeoutMs, customMessage || errorMessage);
}

/**
 * Retry an operation with timeout protection
 * Combines retry logic with timeout for each attempt
 *
 * @param operation - Function that returns a promise
 * @param options - Configuration options
 * @returns Promise that resolves/rejects based on retry logic
 *
 * @example
 * ```typescript
 * const result = await retryWithTimeout(
 *   () => fetch('https://api.example.com'),
 *   {
 *     retries: 3,
 *     timeoutMs: 5000,
 *     delayMs: 1000,
 *     errorMessage: 'API request failed'
 *   }
 * );
 * ```
 */
export async function retryWithTimeout<T>(
  operation: () => Promise<T>,
  options: {
    retries?: number;
    timeoutMs: number;
    delayMs?: number;
    errorMessage?: string;
    onRetry?: (attempt: number, error: Error) => void;
  }
): Promise<T> {
  const {
    retries = 3,
    timeoutMs,
    delayMs = 1000,
    errorMessage = 'Operation failed',
    onRetry,
  } = options;

  let lastError: Error;

  // eslint-disable-next-line no-plusplus
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await withTimeout(operation(), timeoutMs, errorMessage);
    } catch (error) {
      lastError = error as Error;

      if (attempt < retries) {
        if (onRetry) {
          onRetry(attempt, lastError);
        }

        // Wait before retrying
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }

  throw lastError!;
}
